/**
 * Type-safe oRPC API client factory.
 *
 * Creates a fully-typed API client from the server's contract router. The
 * contract is fetched on app boot/page load, while HTTP validator caching
 * (ETag/304) keeps repeat loads efficient when unchanged.
 *
 * The client's custom `fetch` wrapper handles:
 * - **Automatic authorization** — attaches the stored JWT access token to every request.
 * - **Transparent token refresh** — on a 401 response, attempts a single refresh
 *   and retries the original request with the new access token.
 * - **Request timeouts** — aborts requests that exceed `REQUEST_TIMEOUT_MS`.
 *   Set to 60s so chat start (file upload + first Gemini call) can complete.
 *
 * @module index
 */

import { refreshAccessToken } from '@api/lib/auth-refresh';
import { storage } from '@api/lib/storage';
import { tryCatch } from '@learnai/tools/try-catch';
import { getAppVersion } from '@learnai/ui/lib/version';
import { createORPCClient } from '@orpc/client';
import type { AnyContractRouter, ContractRouterClient } from '@orpc/contract';
import type { JsonifiedClient } from '@orpc/openapi-client';
import { OpenAPILink } from '@orpc/openapi-client/fetch';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const REQUEST_TIMEOUT_MS = 60_000;
const RELEASE_VERSION_KEY = 'appReleaseVersion';
const RELEASE_GIT_SHA_KEY = 'appReleaseGitSha';

// ---------------------------------------------------------------------------
// Client factory
// ---------------------------------------------------------------------------

export const createApiClient = async <T extends AnyContractRouter>(url: string) => {
	const { version: appVersion, gitSha: appGitSha } = getAppVersion();

	await Promise.all([
		storage.session.set(RELEASE_VERSION_KEY, appVersion),
		storage.session.set(RELEASE_GIT_SHA_KEY, appGitSha),
	]);

	const { error, data } = await tryCatch(fetch(`${url}/api/contract.json`));

	if (error || !data) {
		console.log(error?.message);
		throw new Error('Cannot find contract.json');
	}

	const router = (await data.json()) as T;

	const link = new OpenAPILink(router, {
		url: `${url}/api`,
		async fetch(request, init) {
			// 1. Setup Timeout Controller
			const timeoutController = new AbortController();
			const timeoutId = setTimeout(() => {
				timeoutController.abort(new Error('RPC_TIMEOUT'));
			}, REQUEST_TIMEOUT_MS);

			// Combine all abort signals: the request timeout, the
			// caller's signal from the oRPC Request object, and any
			// signal on the init object. oRPC places the caller's
			// signal on `request.signal`, not `init.signal`.
			const signals: AbortSignal[] = [timeoutController.signal];

			if (request.signal && !request.signal.aborted) {
				signals.push(request.signal);
			}

			const requestInit = init as RequestInit;
			if (requestInit?.signal && !requestInit.signal.aborted) {
				signals.push(requestInit.signal);
			}

			const signal = signals.length > 1 && 'any' in AbortSignal ? AbortSignal.any(signals) : signals[0];

			try {
				const accessToken = await storage.local.get('accessToken');
				const headers = new Headers(request.headers);
				if (accessToken) {
					headers.set('Authorization', `Bearer ${accessToken}`);
				}

				const isGetOrHead = ['GET', 'HEAD'].includes(request.method.toUpperCase());

				// Consume the body once so it can be reused for the retry.
				const body = isGetOrHead ? undefined : await request.blob();

				// ── First attempt ──────────────────────────────────────
				let response = await fetch(request.url, {
					body,
					headers,
					method: request.method,
					signal: signal,
					...init,
				});

				// ── 401 → refresh + retry ──────────────────────────────
				if (response.status === 401) {
					const refreshToken = await storage.local.get('refreshToken');

					if (refreshToken) {
						const newAccessToken = await refreshAccessToken(url, refreshToken);

						if (newAccessToken) {
							const retryHeaders = new Headers(headers);
							retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);

							response = await fetch(request.url, {
								body,
								headers: retryHeaders,
								method: request.method,
								signal: signal,
								...init,
							});
						}
					}
				}

				// ── SSE: clear the timeout for long-lived streams ──────
				// The timeout guards against the initial connection
				// hanging. Once an event-stream response arrives, the
				// connection is meant to stay open indefinitely — clear
				// the timer so it does not kill the stream after REQUEST_TIMEOUT_MS.
				if (response.headers.get('content-type')?.startsWith('text/event-stream')) {
					clearTimeout(timeoutId);
				}

				return response;
			} finally {
				clearTimeout(timeoutId);
			}
		},
	});

	return createORPCClient<JsonifiedClient<ContractRouterClient<T>>>(link);
};
